// src/components/PatientsSinceLastReading.tsx
import React, {useEffect, useState} from 'react'
import {PieChart, Pie, Cell, ResponsiveContainer, Tooltip} from 'recharts'
import axios from 'axios'
import {API_ENDPOINTS} from '../../../../constants/apiConstants'
import {useReduxAuth} from '../../../auth'
import {FaInfoCircle} from 'react-icons/fa'
import {useNavigate} from 'react-router-dom'
import {useMADoctorDropdown} from '../../context/MADoctorDropdownContext'
import {USER_ROLES} from '../../../../constants/StatusConstants'

// Define interfaces for our chart data and API response.
interface ChartDatum {
  name: string
  value: number
  color: string
}

interface PatientsData {
  totalPatients: number
  totalNeverTaken: number
  group1Within24: number
  group2Between24And48: number
  group3Between48And72: number
  group4GreaterEqual72: number
  group5OlderThan72: number
}

interface CustomTooltipProps {
  active?: boolean
  payload?: any[]
}

// TODO: pull these from your SCSS variables for a single source of truth
const COLORS: Record<string, string> = {
  l24: '#22c55e',
  '24to48': '#60a5fa',
  '48to72': '#f59e0b',
  g72: '#dc2626',
  'No readings': '#64748b',
}

const segmentToFilterMap: Record<string, string> = {
  '< 24': '<24',
  '24–48': '24-48',
  '48–72': '48-72',
  '> 72': '>72',
  'No readings': 'never',
}

const CustomTooltip: React.FC<CustomTooltipProps> = ({active, payload}) => {
  if (active && payload && payload.length) {
    const {name, value, color} = payload[0].payload as ChartDatum
    return (
      <div className='bg-white p-3 rounded-4 shadow-lg border border-light-subtle'>
        <div className='d-flex align-items-center gap-2 mb-2'>
          <div
            style={{
              width: 12,
              height: 12,
              backgroundColor: color,
              borderRadius: '50%',
            }}
          />
          <strong className='text-dark'>{name}</strong>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <FaInfoCircle className='text-muted' />
          <span className='text-muted small'>
            {value} patient{value !== 1 ? 's' : ''}
          </span>
        </div>
      </div>
    )
  }
  return null
}

const PatientsSinceLastReading: React.FC = () => {
  const {currentUser} = useReduxAuth()
  const maDropdown = useMADoctorDropdown()
  const maSelectedDoctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT ? maDropdown.selectedDoctorId : undefined
  
  const navigate = useNavigate()
  const [chartData, setChartData] = useState<ChartDatum[]>([])
  const [total, setTotal] = useState<number>(0)

  const handleSegmentClick = (entry: any) => {
    const name = (entry.payload as ChartDatum).name
    const filterValue = segmentToFilterMap[name]
    if (filterValue) {
      navigate(`/patient?lastReading=${encodeURIComponent(filterValue)}`)
    }
  }

  useEffect(() => {
    const fetchData = async () => {
      // Skip if MA and no doctor selected
      if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && !maSelectedDoctorId) {
        setChartData([])
        setTotal(0)
        return
      }

      try {
        // For MA, use selected doctor ID; for others, use currentUser.id
        const doctorId = currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId
          ? maSelectedDoctorId
          : currentUser?.id

        if (!doctorId) return

        // Build query params for MA
        const queryParams = new URLSearchParams()
        if (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId) {
          queryParams.append('doctor_id', maSelectedDoctorId.toString())
        }

        const res = await axios.get(
          API_ENDPOINTS.GET_PATIENTS_COUNT_SINCE_LAST_READING(doctorId, queryParams.toString()),
          {headers: {Authorization: `Bearer ${currentUser.token}`}}
        )

        const d: PatientsData = res.data?.data || {}
        const updated: ChartDatum[] = [
          {name: '< 24', value: d.group1Within24 || 0, color: COLORS.l24},
          {name: '24–48', value: d.group2Between24And48 || 0, color: COLORS['24to48']},
          {name: '48–72', value: d.group3Between48And72 || 0, color: COLORS['48to72']},
          {name: '> 72', value: d.group4GreaterEqual72 || 0, color: COLORS.g72},
          {name: 'No readings', value: d.totalNeverTaken || 0, color: COLORS['No readings']},
        ]

        setChartData(updated)
        setTotal(d.totalPatients || 0)
      } catch (err) {}
    }

    if (currentUser?.id || (currentUser?.role_id === USER_ROLES.MEDICAL_ASSISTANT && maSelectedDoctorId)) {
      fetchData()
    }
  }, [currentUser, maSelectedDoctorId])

  return (
    <div className='p-4 bg-white rounded-4 shadow-sm border'>
      <div className='mb-1'>
        <div className='d-flex align-items-center gap-2'>
          <h3>Last Readings</h3>
          <span className='text-muted'>(hours ago)</span>
        </div>
      </div>

      <div className='row g-2'>
        {/* Legend */}
        <div className='col-12 order-2'>
          <ul className='list-unstyled mb-0'>
            {chartData.map((entry, idx) => (
              <li key={idx} className='d-flex align-items-center small' style={{marginBottom: '1px'}}>
                <span
                  className='me-2 rounded-circle d-inline-block'
                  style={{backgroundColor: entry.color, width: 12, height: 12}}
                />
                <div className='d-flex justify-content-between w-100'>
                  <span className='text-muted fs-7'>{entry.name}</span>
                  <span className='fw-medium font-bold fs-6'>{entry.value}</span>
                </div>
              </li>
            ))}
          </ul>
        </div>

        {/* Pie Chart */}
        <div className='col-12 order-1'>
          <div className='position-relative mx-auto chart-height'>
            <ResponsiveContainer width='100%' height='100%'>
              <PieChart>
                <Tooltip
                  content={<CustomTooltip />}
                  cursor={{fill: 'rgba(0,0,0,0.05)'}}
                  position={{x: 20, y: 0}}
                />
                <Pie
                  data={chartData}
                  dataKey='value'
                  innerRadius='50%'
                  outerRadius='85%'
                  startAngle={90}
                  endAngle={450}
                  paddingAngle={0} // ← no gap
                  stroke='none' // ← remove any border stroke
                  onClick={handleSegmentClick}
                  cursor='pointer'
                >
                  {chartData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Pie>
              </PieChart>
            </ResponsiveContainer>

            <div className='position-absolute top-50 start-50 translate-middle text-center'>
              {/* <div className='text-muted small mb-1'>Total Patients</div> */}
              <div className='h1 fw-bold'>{total}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default PatientsSinceLastReading
